import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from '@/lib/swal';
import { usePermissions } from '@/hooks/use-permissions';
import { inspectionReportService, type ReportSettings } from '@/services/inspection-report.service';
import { reportNumberFormatPattern } from '@/features/inspection/report/report-workflow';

function extractMsg(error: Error, fallback: string): string {
  if (axios.isAxiosError(error)) {
    const body = error.response?.data as
      | {
          error?: { message?: string };
          message?: string | string[];
        }
      | undefined;
    if (body?.error?.message) return body.error.message;
    if (Array.isArray(body?.message)) return body!.message.join(', ');
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

const SEPARATORS: ReportSettings['reportNumberSeparator'][] = [
  'SLASH',
  'HYPHEN',
  'UNDERSCORE',
  'NONE',
];
const YEAR_FORMATS: ReportSettings['reportNumberYearFormat'][] = ['NONE', 'YY', 'YYYY'];
const RESET_POLICIES: ReportSettings['reportNumberResetPolicy'][] = ['NEVER', 'YEARLY', 'MONTHLY'];
const SEQUENCE_LENGTHS = [3, 4, 5, 6, 7, 8];

const selectClass =
  'w-full rounded-lg border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

export function ReportNumberingSection() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const canManage = has('company-profile:manage');
  const [form, setForm] = useState<ReportSettings | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['report-settings'],
    queryFn: () => inspectionReportService.getSettings(),
    staleTime: 30_000,
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  const mutation = useMutation({
    mutationFn: (dto: ReportSettings) => inspectionReportService.updateSettings(dto),
    onSuccess: (saved) => {
      toast.success('Report numbering saved');
      setForm(saved);
      // The save response already carries the recomputed `nextReportNumber`, so
      // seed the cache with it: the displayed number must update the instant the
      // new minimum is stored, not one refetch later. The invalidate then
      // reconciles with the server in the background.
      qc.setQueryData(['report-settings'], saved);
      void qc.invalidateQueries({ queryKey: ['report-settings'] });
    },
    onError: (e: Error) => toast.error(extractMsg(e, 'Failed to save report numbering')),
  });

  if (isLoading || !form) {
    return (
      <div className="rounded-lg border border-border bg-card p-5">
        <div
          className="h-40 animate-pulse rounded bg-muted"
          data-testid="report-settings-loading"
        />
      </div>
    );
  }

  const set = <K extends keyof ReportSettings>(key: K, value: ReportSettings[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));
  const disabled = !canManage;
  // Compared against the server's copy, so "dirty" means "the saved settings —
  // and therefore the next number below — no longer match what is on screen".
  const dirty = !!data && JSON.stringify(data) !== JSON.stringify(form);

  return (
    <form
      className="rounded-lg border border-border bg-card"
      data-testid="report-numbering-section"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(form);
      }}
    >
      <div className="border-b border-border px-5 py-3">
        <h2 className="text-sm font-semibold text-foreground">Inspection Report Numbering</h2>
        <p className="text-xs text-muted-foreground">
          Applies to future reports only — existing report numbers never change.
        </p>
      </div>

      <div className="space-y-4 p-5">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Report Title
          </label>
          <Input
            value={form.inspectionReportTitle}
            maxLength={255}
            disabled={disabled}
            onChange={(e) => set('inspectionReportTitle', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Prefix</label>
            <Input
              value={form.reportNumberPrefix}
              maxLength={10}
              placeholder="e.g. AST"
              disabled={disabled}
              onChange={(e) => set('reportNumberPrefix', e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Separator
            </label>
            <select
              className={selectClass}
              value={form.reportNumberSeparator}
              disabled={disabled}
              onChange={(e) =>
                set(
                  'reportNumberSeparator',
                  e.target.value as ReportSettings['reportNumberSeparator'],
                )
              }
            >
              {SEPARATORS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Year Format
            </label>
            <select
              className={selectClass}
              value={form.reportNumberYearFormat}
              disabled={disabled}
              onChange={(e) =>
                set(
                  'reportNumberYearFormat',
                  e.target.value as ReportSettings['reportNumberYearFormat'],
                )
              }
            >
              {YEAR_FORMATS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Sequence Length
            </label>
            <select
              className={selectClass}
              value={form.reportNumberSequenceLength}
              disabled={disabled}
              onChange={(e) => set('reportNumberSequenceLength', Number(e.target.value))}
            >
              {SEQUENCE_LENGTHS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          {/*
            Renamed from "Starting Number". The value is no longer a one-time
            seed consulted when the counter is created — it is a live minimum
            applied to every allocation, so on a workspace that has already
            issued reports "starting" described the wrong thing entirely.
          */}
          <div>
            <label
              className="mb-1 block text-xs font-medium text-muted-foreground"
              htmlFor="report-number-minimum"
            >
              Next Report Number (Minimum)
            </label>
            <Input
              id="report-number-minimum"
              type="number"
              min={0}
              value={form.reportNumberStartingSequence}
              disabled={disabled}
              onChange={(e) => set('reportNumberStartingSequence', Number(e.target.value))}
              aria-describedby="report-number-minimum-help"
            />
            <p
              id="report-number-minimum-help"
              className="mt-1 text-xs text-muted-foreground"
              data-testid="report-number-minimum-help"
            >
              The next report will use this number, or continue from the last issued number if that
              is higher. Numbering never goes backwards.
            </p>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Reset Policy
            </label>
            <select
              className={selectClass}
              value={form.reportNumberResetPolicy}
              disabled={disabled}
              onChange={(e) =>
                set(
                  'reportNumberResetPolicy',
                  e.target.value as ReportSettings['reportNumberResetPolicy'],
                )
              }
            >
              {RESET_POLICIES.map((p) => (
                <option key={p} value={p}>
                  {p === 'MONTHLY' ? 'MONTHLY (future ready)' : p}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/*
          "Next report" is the SERVER's answer, read from the settings response,
          never recomputed here. The browser cannot see the sequence counter, and
          reproducing GREATEST(current + 1, minimum) in TypeScript would put the
          allocation rule in two languages that could disagree.

          It therefore describes the SAVED settings. While the form is dirty the
          figure is still true of what is stored, so it is shown with a note
          rather than blanked or — worse — recalculated locally from the unsaved
          input, which would state a number the allocator would not produce.
        */}
        <div className="rounded-lg border border-dashed border-border bg-muted/40 px-4 py-3">
          <p className="text-xs text-muted-foreground">Format</p>
          <p
            className="font-mono text-base font-semibold text-foreground"
            data-testid="report-number-format"
          >
            {reportNumberFormatPattern(form)}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">The next report will be numbered</p>
          <p
            className="font-mono text-sm font-medium text-foreground"
            data-testid="report-number-next"
          >
            {data?.nextReportNumber ?? '—'}
          </p>
          {dirty && (
            <p
              className="mt-1 text-xs text-muted-foreground"
              data-testid="report-number-next-stale"
            >
              Save to see the number your changes will produce.
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            Numbers already issued keep the number and format they were created with.
          </p>
        </div>

        {canManage && (
          <div className="flex justify-end">
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        )}
      </div>
    </form>
  );
}
